/**
 * The prototype of a board, pure: its screens (top-level frames that are not components), what
 * each shows, placed inside it, and where each click leads. Built from the board's own geometry
 * (`state/board`: places, auto layout; `components.ts`: instances), so the prototype is what the
 * board shows.
 */
import type { Graph } from "@protocol/Graph";
import { pictureOf } from "../../../shared/assets";
import { CARD_LOOK, showsWords, type BoardLook, type ElementDef, type Style } from "../../../shared/elements";
import { boardOf, contentsOf } from "../../../state/board";
import { instanceParts, type InstancePart } from "../components";
import { DEVICES } from "../constants";
import { screensOf } from "../screens";

/** What node kind `kind` is on the board (`KindsService.board`). */
export type LookOf = (kind: string) => BoardLook;

/** One thing a screen shows, placed from the screen's corner. */
export interface ProtoElement {
  id: string;
  /** What it is drawn as (`"box"`: a frame inside the screen). */
  shape: string;
  /** That element (a box: none). */
  def: ElementDef | null;
  /** The kind id (for a box: empty). */
  kind: string;
  /** Its words. */
  text: string;
  /** Whether its words are drawn on it (a UI rectangle's name is only its layer's). */
  wordsShown: boolean;
  x: number;
  y: number;
  w: number;
  h: number;
  rotate: number;
  style: Style;
  /** The project file a picture shows (its first attachment). */
  picture: string | null;
  /** The screen a click leads to, `"back"`, or `null`. */
  go: string | null;
  /** How the next screen comes in: `instant`, `dissolve` or `slide`. */
  transition: string;
  /** An instance's parts, placed from its corner; and the look of its component's frame. */
  parts: ProtoElement[];
  frame: Style | null;
}

/** One screen of the prototype. */
export interface ProtoScreen {
  id: string;
  name: string;
  /** What is shown at once: the frame's width, and its device's height when it is taller (it scrolls). */
  viewport: { w: number; h: number };
  /** The whole screen (taller than the viewport: it scrolls). */
  w: number;
  h: number;
  style: Style;
  elements: ProtoElement[];
}

/** A board's prototype. */
export interface Prototype {
  screens: ProtoScreen[];
  /** The screen played first: the one marked `start`, else the first. */
  start: string | null;
}

/** The transitions a click may use. */
export const TRANSITIONS = ["instant", "dissolve", "slide"] as const;

/** An instance part as an element. */
function partElement(part: InstancePart, lookOf: LookOf): ProtoElement {
  const kind = part.node?.kind ?? "";
  const look = part.node ? lookOf(kind) : CARD_LOOK;
  return {
    id: part.id,
    shape: part.node ? look.element : "box",
    def: part.node ? look.def : null,
    kind,
    text: part.text,
    wordsShown: showsWords(look),
    x: part.place.x,
    y: part.place.y,
    w: part.place.w,
    h: part.place.h,
    rotate: part.place.rotate ?? 0,
    style: part.style,
    picture: pictureOf(part.node?.attachments),
    go: null,
    transition: "instant",
    parts: part.parts.map((p) => partElement(p, lookOf)),
    frame: null,
  };
}

/** The prototype of `graph`'s board, each item drawn as `lookOf` says its kind is. */
export function prototypeOf(graph: Graph, lookOf: LookOf): Prototype {
  const content = boardOf(graph, (kind) => lookOf(kind).size);
  const frames = screensOf(content);
  const ids = new Set(frames.map((f) => f.id));
  const screens = frames.map((frame): ProtoScreen => {
    const box = frame.place;
    const style = frame.group.style ?? {};
    const device = DEVICES[style.device ?? ""];
    const { nodes, frames: inner } = contentsOf(graph, frame.id);
    const held = new Set(nodes);
    const at = (p: { x: number; y: number }) => ({ x: p.x - box.x, y: p.y - box.y });
    const boxes = content.frames
      .filter((f) => inner.includes(f.id))
      .map((f): ProtoElement => ({ id: f.id, shape: "box", def: null, kind: "", text: "", wordsShown: false, ...at(f.place), w: f.place.w, h: f.place.h, rotate: 0, style: f.group.style ?? {}, picture: null, go: null, transition: "instant", parts: [], frame: null }));
    const items = content.items
      .filter((i) => held.has(i.id))
      .map((i): ProtoElement => {
        const s = i.node.style ?? {};
        const go = s.navigate === "back" || (s.navigate && ids.has(s.navigate)) ? s.navigate : null;
        const look = lookOf(i.node.kind);
        const instance = look.element === "instance" ? instanceParts(graph, content, i.node, i.place) : null;
        return {
          id: i.id,
          shape: look.element,
          def: look.def,
          kind: i.node.kind,
          text: i.node.name,
          wordsShown: showsWords(look),
          ...at(i.place),
          w: i.place.w,
          h: i.place.h,
          rotate: i.place.rotate ?? 0,
          style: s,
          picture: pictureOf(i.node.attachments),
          go,
          transition: (TRANSITIONS as readonly string[]).includes(s.transition ?? "") ? s.transition! : "instant",
          parts: instance?.parts.map((p) => partElement(p, lookOf)) ?? [],
          frame: instance?.frame ?? null,
        };
      });
    return { id: frame.id, name: frame.group.name, viewport: { w: box.w, h: device ? Math.min(device.h, box.h) : box.h }, w: box.w, h: box.h, style, elements: [...boxes, ...items] };
  });
  return { screens, start: screens.find((s) => s.style.start === "yes")?.id ?? screens[0]?.id ?? null };
}

/** Every project file the prototype's pictures show. */
export function picturesOf(prototype: Prototype): string[] {
  const found = new Set<string>();
  const walk = (elements: ProtoElement[]) => {
    for (const e of elements) {
      if (e.shape === "image" && e.picture) found.add(e.picture);
      walk(e.parts);
    }
  };
  for (const s of prototype.screens) walk(s.elements);
  return [...found];
}
